import React, {
  Fragment,
  useCallback,
  useContext,
  useState,
} from "react";
import { FormattedMessage, useIntl } from "react-intl";
import CountryConfigContext from "../CountryConfigContext";
import useCountryAxiosInstance from "../hooks/useCountryAxiosInstance";
import { NotificationsContext } from "../ui-components/Notifications";
import Button from "../ui-components/Button";
import WorksiteDetailSurveyBlock from "./WorksiteDetailSurveyBlock";
import TranslatedKey from "./TranslatedKey";
import {isAssociatedServiceVisibile} from "@feature/surveys/lib/design-quotation/permissions";

const WorksiteDetailNewServicesList = ({
  worksite,
  servicesSurveys,
  setServicesSurveys,
  setDeleteSurvey,
  userData,
  creators,
  setApExportSurvey,
  setCreateFromDesignModalData,
  setEditedSurveyData,
  activeTab,
  submittingSurvey,
}) => {

  // context
  const {
    config: { services },
  } = useContext(CountryConfigContext);
  const { push } = useContext(NotificationsContext);

  // hooks
  const intl = useIntl();
  const apiRequest = useCountryAxiosInstance();

  // state
  const [isDuplicatingSurvey, setIsDuplicatingSurvey] = useState(servicesSurveys.reduce(
    (acc, curr) => ({ ...acc, [curr.id]: false }),
    {}
  ));

  // utilities
  // --- services are fetched before login via public API, let's manage visibility in this lazy way
  const configuratorServices = services.filter((s) =>
    s.type === "configurator"
    && s.class === activeTab
    && (
      s.r_perm.includes("public")
      || s.r_perm.includes(userData.role_type)
      || s.r_perm.includes(userData.account_user_role_type)
      || userData.groups.some((g)  => s.r_perm.includes("group_" + g))
    )
  );

  const duplicateSurvey = useCallback(async (surveyId) => {
    if (!surveyId) return;

    setIsDuplicatingSurvey((prev) => ({ ...prev, [surveyId]: true }));
    try {
      const result = await apiRequest.post(`/service_surveys/duplicate/${surveyId}`, {});
      setServicesSurveys((prev) => [result, ...prev]);
      push({
        type: "success",
        title: intl.formatMessage({
          id: "survey_successfully_duplicated",
          defaultMessage: "Configurazione duplicata correttamente",
        })
      });
    }
    catch (e) {
      push({type: "error", title: intl.formatMessage({id: "server_error"})});
      throw e;
    }
    finally {
      setIsDuplicatingSurvey((prev) => ({...prev, [surveyId]: false}));
    }
  })

  if (!configuratorServices.length) {
    return (
      <FormattedMessage
        id="no_services_available"
        defaultMessage="Non sono presenti configuratori in questo momento"
      />
    );
  }

  return (
    <>
      <Fragment>
        <div className="flex flex-wrap mt-6 w-auto" style={{ margin: "-1%" }}>
          {configuratorServices.map((service) => {
            const surveys = servicesSurveys?.filter(
              (survey) => survey.service_id === service.id
            );
            const associatedServices = services.filter(
              ({ class: serviceClass, associated_services, active }) =>
                serviceClass === 1 /* Quotazione */ &&
                isAssociatedServiceVisibile(
                  userData,
                  associated_services[service.id]?.r_perm || [],
                ) &&
                active
              );

            let bg;
            if (service.class === 0 /* Progettazione */) {
              bg = "bg-teal";
            } else if (service.class === 1 /* Quotazione */) {
              bg = `bg-${userData.business}-400`;
            } else if (service.class === 2 /* Servizi */) {
              bg = "bg-ivory";
            } else {
              bg = "bg-gray-200";
            }

            return (
              <div
                key={service.id}
                style={{ margin: "1%" }}
                className={`${bg} flex flex-col cursor-pointer w-98 md:w-48 `}
              >
                <div className="items-center justify-between p-4 flex">
                  <div className="flex flex-col mr-4">
                    <p
                      style={{ fontSize: "17px" }}
                      className="font-semibold text-am-700 uppercase leading-tight"
                    >
                      <FormattedMessage
                        id={service.name}
                        defaultMessage={service.name}
                      />
                    </p>
                    {service.description ? (
                      <div>
                        <p className="text-am-700 mt-1">
                          <TranslatedKey id={service.description} />
                        </p>
                      </div>
                    ) : null}
                  </div>
                  <div>
                    <Button
                      onClick={() => {
                        setEditedSurveyData({
                          service,
                          survey: null,  // survey to be created
                          designSurveys: service.class === 1 /* Quotazione */
                            ? servicesSurveys.filter(
                              (ss) => Object.keys(service.associated_services).includes(ss.service_id.toString())
                            )
                            : [],
                        })
                      }}
                      styleType="white"
                      disabled={submittingSurvey}
                    >
                      <div className="hidden lg:inline">
                        <FormattedMessage id="add" />
                      </div>
                      <div className="inline lg:hidden">
                        <FormattedMessage id="+" />
                      </div>
                    </Button>
                  </div>
                </div>
                {surveys?.length ? (
                  <div
                    style={{ maxHeight: "200px", overflowY: "auto" }}
                    className="w-full bg-white h-full border"
                  >
                    {surveys.map((survey, index) => (
                      <WorksiteDetailSurveyBlock
                        key={survey.id}
                        setDeleteSurvey={setDeleteSurvey}
                        worksite={worksite}
                        survey={survey}
                        last={index === surveys.length - 1}
                        service={service}
                        associatedServices={associatedServices}
                        creators={creators}
                        setApExportSurvey={setApExportSurvey}
                        setEditedSurveyData={setEditedSurveyData}
                        setCreateFromDesignModalData={setCreateFromDesignModalData}
                        duplicateSurvey={duplicateSurvey}
                        isDuplicatingSurvey={isDuplicatingSurvey[survey.id]}
                        setServicesSurveys={setServicesSurveys}
                      />
                    ))}
                  </div>
                ) : null}
              </div>
            );
          })}
        </div>
      </Fragment>
    </>
  );
};

export default WorksiteDetailNewServicesList;
